import { PDFDocument } from 'pdf-lib';

/**
 * 壓縮 PDF 文件 - 增強版
 * @param {File} file - PDF 文件
 * @param {string} level - 壓縮級別 ('low', 'medium', 'high')
 * @param {Function} onProgress - 進度回調
 * @returns {Promise<object>} 壓縮結果
 */
export const compressPdf = async (file, level = 'medium', onProgress = null) => {
  try {
    if (onProgress) onProgress(10, '讀取 PDF 文件...');
    
    const arrayBuffer = await file.arrayBuffer();
    
    if (onProgress) onProgress(30, '解析 PDF 結構...');
    
    const pdfDoc = await PDFDocument.load(arrayBuffer, {
      ignoreEncryption: true,
      updateMetadata: false
    });
    
    if (onProgress) onProgress(50, '優化 PDF 對象...');
    
    // 獲取壓縮選項
    const options = getCompressionOptions(level);
    
    // 移除未使用的對象
    if (options.removeUnusedObjects) {
      await removeUnusedObjects(pdfDoc);
    }
    
    if (onProgress) onProgress(70, '壓縮 PDF 數據...');
    
    // 保存壓縮後的 PDF
    const compressedPdfBytes = await pdfDoc.save({
      useObjectStreams: options.useObjectStreams,
      addDefaultPage: false,
      objectsPerTick: options.objectsPerTick
    });
    
    if (onProgress) onProgress(90, '生成壓縮文件...');
    
    const blob = new Blob([compressedPdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    
    // 計算壓縮效果
    const originalSize = file.size;
    const compressedSize = blob.size;
    const compressionRatio = Math.round((1 - compressedSize / originalSize) * 100);
    
    if (onProgress) onProgress(100, '完成');
    
    console.log('PDF 壓縮結果:', {
      原始大小: formatBytes(originalSize),
      壓縮後大小: formatBytes(compressedSize),
      壓縮率: compressionRatio + '%',
      節省空間: formatBytes(originalSize - compressedSize)
    });
    
    return {
      blob,
      url,
      size: blob.size,
      originalSize,
      compressionRatio
    };
  } catch (error) {
    console.error('PDF 壓縮錯誤:', error);
    
    // 提供更詳細的錯誤信息
    let errorMessage = 'PDF 壓縮失敗';
    
    if (error.message.includes('encrypted')) {
      errorMessage = 'PDF 文件已加密,無法壓縮';
    } else if (error.message.includes('Invalid')) {
      errorMessage = 'PDF 文件格式無效';
    } else if (error.message.includes('password')) {
      errorMessage = 'PDF 文件需要密碼';
    }
    
    throw new Error(errorMessage);
  }
};

/**
 * 移除未使用的 PDF 對象
 * @param {PDFDocument} pdfDoc - PDF 文檔
 */
const removeUnusedObjects = async (pdfDoc) => {
  try {
    // 移除元數據以減小文件大小
    const metadata = [
      'Title',
      'Author',
      'Subject',
      'Keywords',
      'Creator',
      'Producer'
    ];
    
    metadata.forEach(key => {
      try {
        pdfDoc.setTitle('');
        pdfDoc.setAuthor('');
        pdfDoc.setSubject('');
        pdfDoc.setKeywords([]);
        pdfDoc.setCreator('');
        pdfDoc.setProducer('');
      } catch (e) {
        // 忽略錯誤
      }
    });
  } catch (error) {
    console.warn('移除未使用對象時出錯:', error);
  }
};

/**
 * 獲取壓縮選項
 * @param {string} level - 壓縮級別
 * @returns {object} 壓縮選項
 */
const getCompressionOptions = (level) => {
  switch (level) {
    case 'low':
      return {
        useObjectStreams: true,
        objectsPerTick: 50,
        removeUnusedObjects: false
      };
    case 'high':
      return {
        useObjectStreams: true,
        objectsPerTick: 1000,
        removeUnusedObjects: true
      };
    case 'medium':
    default:
      return {
        useObjectStreams: true,
        objectsPerTick: 500,
        removeUnusedObjects: true
      };
  }
};

/**
 * 格式化字節大小
 * @param {number} bytes - 字節數
 * @returns {string} 格式化後的大小
 */
const formatBytes = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};